// src/components/NotificationSettings.jsx
// Push notification settings panel for user preferences

import React, { useState, useEffect } from 'react';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  getPushStatus,
  sendTestNotification,
  getNotificationPreferences,
  updateNotificationPreferences,
  registerServiceWorker
} from '../utils/pushNotifications';

const NotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);

  // Push status
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [pushConfigured, setPushConfigured] = useState(false);
  const [devices, setDevices] = useState([]);

  // Preferences
  const [preferences, setPreferences] = useState({
    risk_alerts: true,
    sms_received: true,
    calls_missed: true,
    voicemails: true,
    task_reminders: true,
    pulse_responses: true,
    enrollment_deadlines: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00'
  });

  // Message state
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      // Check browser support
      const isSupported = isPushSupported();
      setSupported(isSupported);

      if (!isSupported) {
        setLoading(false);
        return;
      }

      // Check permission
      const perm = getNotificationPermission();
      setPermission(perm);

      // Register service worker if needed
      await registerServiceWorker();

      // Check subscription status
      const isSub = await isSubscribedToPush();
      setSubscribed(isSub);

      // Get server status
      const status = await getPushStatus();
      if (status) {
        setPushConfigured(status.pushConfigured);
        setDevices(status.devices || []);
      }

      // Get preferences
      const prefs = await getNotificationPreferences();
      if (prefs) {
        setPreferences(prefs);
      }
    } catch (err) {
      console.error('[NotificationSettings] Load error:', err);
    }
    setLoading(false);
  };

  const handleEnablePush = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const result = await subscribeToPush();

      if (result.success) {
        setSubscribed(true);
        setPermission('granted');
        setMessage({ type: 'success', text: 'Push notifications enabled!' });
        await loadStatus(); // Refresh status
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to enable notifications' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }

    setSaving(false);
  };

  const handleDisablePush = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const result = await unsubscribeFromPush();

      if (result.success) {
        setSubscribed(false);
        setDevices([]);
        setMessage({ type: 'success', text: 'Push notifications disabled' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to disable notifications' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }

    setSaving(false);
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);

    // Auto-save
    const success = await updateNotificationPreferences({ [key]: value });
    if (!success) {
      // Revert on failure
      setPreferences(preferences);
      setMessage({ type: 'error', text: 'Failed to save preference' });
    }
  };

  const handleTestNotification = async () => {
    setTestSending(true);
    setMessage(null);

    try {
      const result = await sendTestNotification();

      if (result.ok) {
        if (result.result?.sent > 0) {
          setMessage({ type: 'success', text: 'Test notification sent!' });
        } else {
          setMessage({ type: 'warning', text: `Not sent: ${result.result?.reason || 'unknown'}` });
        }
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send test' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }

    setTestSending(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Push Notifications</h2>
        <p className="text-sm text-gray-500 mt-1">
          Get instant alerts on your phone or desktop
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mx-6 mt-4 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          message.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Browser Support Check */}
        {!supported ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Push notifications are not supported in this browser.
              Try using Chrome, Edge, Firefox, or Safari 16.4+.
            </p>
          </div>
        ) : !pushConfigured ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Push notifications are not yet configured on the server.
              Contact support for setup.
            </p>
          </div>
        ) : (
          <>
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Enable Push Notifications</h3>
                <p className="text-sm text-gray-500">
                  {subscribed
                    ? `Active on ${devices.length} device(s)`
                    : 'Receive alerts even when Pro IRP is closed'}
                </p>
              </div>
              <button
                onClick={subscribed ? handleDisablePush : handleEnablePush}
                disabled={saving || permission === 'denied'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  subscribed ? 'bg-[#172A3A]' : 'bg-gray-300'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    subscribed ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {permission === 'denied' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                Notifications are blocked. Please enable them in your browser settings.
              </div>
            )}

            {/* Test Button */}
            {subscribed && (
              <button
                onClick={handleTestNotification}
                disabled={testSending}
                className="text-sm text-[#172A3A] hover:underline disabled:opacity-50"
              >
                {testSending ? 'Sending...' : 'Send test notification'}
              </button>
            )}

            {/* Notification Types */}
            {subscribed && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-medium text-gray-900 mb-4">Notification Types</h3>
                <div className="space-y-4">
                  <NotificationToggle
                    label="Risk Alerts"
                    description="High risk score warnings and emergency alerts"
                    checked={preferences.risk_alerts}
                    onChange={(v) => handlePreferenceChange('risk_alerts', v)}
                  />
                  <NotificationToggle
                    label="SMS Received"
                    description="When a client sends you a text message"
                    checked={preferences.sms_received}
                    onChange={(v) => handlePreferenceChange('sms_received', v)}
                  />
                  <NotificationToggle
                    label="Missed Calls"
                    description="When you miss a call from a client"
                    checked={preferences.calls_missed}
                    onChange={(v) => handlePreferenceChange('calls_missed', v)}
                  />
                  <NotificationToggle
                    label="Voicemails"
                    description="When a client leaves a voicemail"
                    checked={preferences.voicemails}
                    onChange={(v) => handlePreferenceChange('voicemails', v)}
                  />
                  <NotificationToggle
                    label="Task Reminders"
                    description="Reminders for upcoming due tasks"
                    checked={preferences.task_reminders}
                    onChange={(v) => handlePreferenceChange('task_reminders', v)}
                  />
                  <NotificationToggle
                    label="Pulse Responses"
                    description="When a client responds to a pulse check"
                    checked={preferences.pulse_responses}
                    onChange={(v) => handlePreferenceChange('pulse_responses', v)}
                  />
                </div>
              </div>
            )}

            {/* Quiet Hours */}
            {subscribed && (
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Quiet Hours</h3>
                    <p className="text-sm text-gray-500">Pause notifications during these hours</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('quiet_hours_enabled', !preferences.quiet_hours_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.quiet_hours_enabled ? 'bg-[#172A3A]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.quiet_hours_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {preferences.quiet_hours_enabled && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600">From</span>
                    <input
                      type="time"
                      value={preferences.quiet_hours_start}
                      onChange={(e) => handlePreferenceChange('quiet_hours_start', e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1.5"
                    />
                    <span className="text-gray-600">to</span>
                    <input
                      type="time"
                      value={preferences.quiet_hours_end}
                      onChange={(e) => handlePreferenceChange('quiet_hours_end', e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1.5"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Active Devices */}
            {subscribed && devices.length > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-medium text-gray-900 mb-3">Active Devices</h3>
                <div className="space-y-2">
                  {devices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{device.deviceName}</span>
                      <span className="text-gray-400">
                        Last used: {new Date(device.lastUsed).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Toggle component for notification types
const NotificationToggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between">
    <div>
      <span className="text-sm font-medium text-gray-800">{label}</span>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-[#172A3A]' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export default NotificationSettings;
